import { AccountFeatureFlag } from '@/services/auth/feature-flags';
import {
  PROMO_CHECKOUT_PATH,
  requiresCardCheckout,
  type RequiresCardCheckoutAccount,
} from '@/services/auth/promo-checkout';
import type { RawAccount } from '@/services/auth/types';
import { isEligibleForOnboarding } from './completion';
import { ONBOARDING_PATHS } from './paths';

/** Just the raw-payload fields the selector reads; a full `RawAccount` satisfies it. */
export type PostSignupAccount = Pick<RawAccount, 'feature_flags' | 'account_state' | 'plan_slug' | 'created_at'> &
  RequiresCardCheckoutAccount;

/**
 * Whether the new account belongs to the onboarding cohort. Takes the raw
 * payload because this runs before the auth stores are populated, so
 * `useAuth()` and the hooks built on it would throw.
 */
export function isPostSignupOnboardingCohort(account: PostSignupAccount): boolean {
  return isEligibleForOnboarding({
    isFlagEnabled: account.feature_flags?.[AccountFeatureFlag.EssentialsOnboarding] === true,
    account,
  });
}

/**
 * Where signup sends the new account. A promo account that still owes a card
 * goes to checkout first: the wizard sits outside `_authenticated`, so the
 * app-entry gate that enforces the card everywhere else never sees it, and the
 * flow is signup → checkout → wizard. Everyone else goes to the wizard or the
 * app by cohort.
 */
export function selectPostSignupDestination(account: PostSignupAccount): string {
  if (requiresCardCheckout(account)) {
    return PROMO_CHECKOUT_PATH;
  }
  return isPostSignupOnboardingCohort(account) ? ONBOARDING_PATHS.wizard : '/';
}
